// Scene metadata: fetched from the DB-backed `/api/scenes` endpoint. Replaces
// the previous static import from `public/scene-data.js`. Polygon / translation
// data still loads separately via `/api/scenes/:slug/items` in LessonPage.
//
// A module-level promise caches the scenes list so multiple components on the
// same route don't fire duplicate requests.

import { useEffect, useState } from "react";

export type Language = {
  label: string;
  locale: string;
  direction?: "ltr" | "rtl";
};

export type Translation = {
  text: string;
  phonetic: string;
};

export type PolygonShape = {
  type: "polygon";
  points: [number, number][];
};

export type SceneItem = {
  id: string;
  english: string;
  shape: PolygonShape;
  translations: Record<string, Translation>;
};

export type Scene = {
  slug: string;
  label: string;
  title: string;
  alt: string;
  image: string;
  previewImage?: string;
  width: number;
  height: number;
  itemCount: number;
};

// Static language metadata. Kept in code rather than the DB — only 4 entries,
// not admin-managed, and every page needs them synchronously to render labels
// before any fetch settles.
export const languages: Record<string, Language> = {
  es: { label: "Spanish", locale: "es-ES" },
  fr: { label: "French", locale: "fr-FR" },
  ja: { label: "Japanese", locale: "ja-JP" },
  ar: { label: "العربية", locale: "ar", direction: "rtl" },
};

export function isRtlLanguage(code: string): boolean {
  return languages[code]?.direction === "rtl";
}

let scenesPromise: Promise<Scene[]> | null = null;
const invalidationListeners = new Set<() => void>();

function fetchScenes(): Promise<Scene[]> {
  if (!scenesPromise) {
    scenesPromise = fetch("/api/scenes", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { scenes: [] }))
      .then((data) => (data.scenes ?? []) as Scene[])
      .catch(() => []);
  }
  return scenesPromise;
}

// Clears the cached scenes list and notifies every mounted useScenes() hook
// to refetch. Call after a scene CRUD operation (create/regenerate/delete)
// so all pages showing the list stay in sync without a hard reload.
export function invalidateScenes(): void {
  scenesPromise = null;
  for (const listener of invalidationListeners) listener();
}

// React hook: returns the scenes list, or null while still loading.
export function useScenes(): Scene[] | null {
  const [scenes, setScenes] = useState<Scene[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetchScenes().then((list) => {
        if (!cancelled) setScenes(list);
      });
    };
    load();
    invalidationListeners.add(load);
    return () => {
      cancelled = true;
      invalidationListeners.delete(load);
    };
  }, []);
  return scenes;
}

// React hook: one scene by slug. Returns null while loading OR if the slug
// doesn't match any scene. Callers must disambiguate by checking the list
// returned from `useScenes()` if they need to tell loading from not-found.
export function useScene(slug: string | undefined): Scene | null {
  const scenes = useScenes();
  if (!scenes || !slug) return null;
  return scenes.find((s) => s.slug === slug) ?? null;
}
